<template>
  <section class="about-landing">
    <header class="about-landing-nav" :class="{ 'is-compact': isNavCompact }">
      <RouterLink class="about-landing-brand" to="/about" aria-label="Raider.io Dashboard home">
        <span class="about-landing-logo">
          <img :src="appLogo" alt="">
          <small>2.0</small>
        </span>
        <span>
          <small>World of Warcraft</small>
          <strong>Raider.io Dashboard</strong>
        </span>
      </RouterLink>

      <nav class="about-anchor-nav" aria-label="About page sections">
        <button type="button" @click="scrollToSection('features')">Features</button>
        <button type="button" @click="scrollToSection('workflows')">Workflows</button>
        <button type="button" @click="scrollToSection('armory')">Armory</button>
        <button type="button" @click="scrollToSection('sources')">Data sources</button>
      </nav>

      <div class="about-nav-actions">
        <RouterLink class="btn" to="/mythic-plus">
          Open dashboard
          <i class="fa-solid fa-arrow-right"></i>
        </RouterLink>
      </div>
    </header>

    <main>
      <section class="about-landing-hero">
        <div class="about-hero-copy">
          <div class="about-release-pill">
            <span></span>
            Season-ready Warcraft intelligence
          </div>
          <h1>Your next run.<br><em>Already mapped.</em></h1>
          <p>
            A focused World of Warcraft command center for Mythic+ progression, raid races,
            character watchlists, build research and the news that changes how you play.
          </p>
          <div class="about-hero-actions">
            <RouterLink class="btn about-primary-cta" to="/mythic-plus">
              Explore live data
              <i class="fa-solid fa-arrow-right"></i>
            </RouterLink>
            <button class="btn-outline" type="button" @click="scrollToSection('features')">
              See how it works
              <i class="fa-solid fa-arrow-down"></i>
            </button>
          </div>
          <ul class="about-hero-notes" aria-label="Product highlights">
            <li><i class="fa-solid fa-circle-check"></i> No account required</li>
            <li><i class="fa-solid fa-circle-check"></i> Installable PWA</li>
            <li><i class="fa-solid fa-circle-check"></i> Powered by Raider.io data</li>
          </ul>
        </div>

        <div class="about-hero-stage" aria-label="Mythic Plus dashboard preview">
          <span class="about-orbit about-orbit-one" aria-hidden="true"></span>
          <span class="about-orbit about-orbit-two" aria-hidden="true"></span>
          <button class="about-browser about-browser-hero" type="button" @click="openScreenshot(heroScreen)">
            <span class="about-browser-bar">
              <span class="about-browser-dots"><i></i><i></i><i></i></span>
              <span class="about-browser-address">/mythic-plus</span>
              <span class="about-browser-action"><i class="fa-solid fa-up-right-and-down-left-from-center"></i></span>
            </span>
            <span class="about-browser-viewport">
              <img :src="heroScreen.image" :alt="heroScreen.alt" fetchpriority="high">
            </span>
          </button>
          <span class="about-floating-stat about-floating-stat-score">
            <small>Top 0.1%</small>
            <strong>4234</strong>
            <em>season cutoff</em>
          </span>
          <span class="about-floating-stat about-floating-stat-raid">
            <i class="fa-solid fa-dungeon"></i>
            <span><strong>92 teams</strong><small>Timed +25 keys</small></span>
          </span>
        </div>
      </section>

      <section class="about-proof-strip" aria-label="Dashboard capabilities">
        <article v-for="(proof, index) in proofPoints" :key="proof.label">
          <span class="about-proof-icon" aria-hidden="true">
            <i :class="proof.icon"></i>
            <b>0{{ index + 1 }}</b>
          </span>
          <span><strong>{{ proof.value }}</strong><small>{{ proof.label }}</small></span>
        </article>
      </section>

      <section id="features" class="about-intro-section">
        <div class="about-intro-context">
          <p class="about-section-kicker">Everything that matters, in context</p>
          <div class="about-context-questions" aria-label="Questions the dashboard helps answer">
            <span v-for="question in contextQuestions" :key="question.text">
              <i :class="question.icon"></i>
              <small>{{ question.label }}</small>
              <strong>{{ question.text }}</strong>
            </span>
          </div>
        </div>
        <div class="about-intro-copy">
          <h2>Stop assembling the story from twelve different tabs.</h2>
          <p>
            Raider.io is the source of truth. This dashboard turns its data into focused views
            built around the questions players actually ask before a key, a raid night or a roster decision.
          </p>
        </div>
      </section>

      <section id="workflows" class="about-product-stories">
        <article
          v-for="(story, index) in productStories"
          :key="story.title"
          class="about-product-story"
          :class="{ 'is-reversed': index % 2 === 1 }"
        >
          <div class="about-story-copy">
            <span class="about-story-index">0{{ index + 1 }}</span>
            <p class="about-section-kicker">{{ story.kicker }}</p>
            <h2>{{ story.title }}</h2>
            <p>{{ story.description }}</p>
            <ul>
              <li v-for="point in story.points" :key="point">
                <i class="fa-solid fa-check"></i>
                {{ point }}
              </li>
            </ul>
            <RouterLink class="about-text-link" :to="story.to">
              {{ story.cta }}
              <i class="fa-solid fa-arrow-right"></i>
            </RouterLink>
          </div>

          <div
            class="about-story-media"
            :class="{ 'has-companion': story.companion, 'has-variants': story.variants }"
          >
            <div
              v-if="story.variants"
              class="about-story-view-switcher"
              role="tablist"
              :aria-label="`${story.kicker} views`"
            >
              <span class="about-story-switcher-context" aria-hidden="true">
                <i class="fa-solid fa-dungeon"></i>
                <span><small>Mythic+ views</small><strong>Season lens</strong></span>
              </span>
              <span class="about-story-switcher-track">
                <span
                  class="about-story-switcher-indicator"
                  :style="{ transform: `translateX(${story.activeVariant * 100}%)` }"
                  aria-hidden="true"
                ></span>
                <button
                  v-for="(variant, variantIndex) in story.variants"
                  :key="variant.label"
                  type="button"
                  role="tab"
                  :class="{ 'is-active': story.activeVariant === variantIndex }"
                  :aria-selected="story.activeVariant === variantIndex"
                  @click="setStoryVariant(story, variantIndex)"
                >
                  <i :class="variant.icon"></i>
                  {{ variant.label }}
                </button>
              </span>
              <span class="about-story-switcher-count" aria-hidden="true">
                0{{ story.activeVariant + 1 }} <small>/ 0{{ story.variants.length }}</small>
              </span>
            </div>

            <button class="about-browser" type="button" @click="openScreenshot(getStoryScreen(story))">
              <span class="about-browser-bar">
                <span class="about-browser-dots"><i></i><i></i><i></i></span>
                <span class="about-browser-address">{{ story.to }}</span>
                <span class="about-browser-action"><i class="fa-solid fa-up-right-and-down-left-from-center"></i></span>
              </span>
              <span class="about-browser-viewport" :style="{ '--shot-position': getStoryScreen(story).position }">
                <Transition :name="`about-story-shot-${story.slideDirection || 'next'}`">
                  <img
                    :key="getStoryScreen(story).image"
                    :src="getStoryScreen(story).image"
                    :alt="getStoryScreen(story).alt"
                    loading="lazy"
                  >
                </Transition>
              </span>
            </button>

            <button
              v-if="story.companion"
              class="about-companion-shot"
              type="button"
              @click="openScreenshot(story.companion)"
            >
              <img :src="story.companion.image" :alt="story.companion.alt" loading="lazy">
              <span><i class="fa-solid fa-magnifying-glass-plus"></i></span>
            </button>
          </div>
        </article>
      </section>

      <section id="armory" class="about-armory-section">
        <header class="about-armory-heading">
          <div>
            <p class="about-section-kicker">From roster to dossier</p>
            <h2>The character behind the score.</h2>
          </div>
          <div class="about-armory-intro">
            <span class="about-armory-journey" aria-label="Favorite character to Armory details">
              <i class="fa-solid fa-star"></i>
              Favorite
              <i class="fa-solid fa-arrow-right"></i>
              <i class="fa-solid fa-shield-halved"></i>
              Armory
            </span>
            <p>
              The Armory button on every saved character opens a focused profile powered by
              Blizzard data—without losing the shortlist that brought you there.
            </p>
          </div>
        </header>

        <div class="about-armory-shell">
          <div class="about-armory-tabs" role="tablist" aria-label="Armory profile views">
            <button
              v-for="(screen, index) in armoryScreens"
              :id="`armory-tab-${index}`"
              :key="screen.title"
              type="button"
              role="tab"
              :aria-selected="activeArmoryIndex === index"
              aria-controls="armory-panel"
              :class="{ 'is-active': activeArmoryIndex === index }"
              @click="setArmoryScreen(index)"
            >
              <i :class="screen.icon"></i>
              <span><strong>{{ screen.title }}</strong><small>{{ screen.label }}</small></span>
              <b>0{{ index + 1 }}</b>
            </button>
          </div>

          <div
            id="armory-panel"
            class="about-armory-viewer"
            role="tabpanel"
            :aria-labelledby="`armory-tab-${activeArmoryIndex}`"
          >
            <button
              class="about-armory-screen"
              type="button"
              :aria-label="`Open ${activeArmoryScreen.title} screenshot`"
              @click="openScreenshot(activeArmoryScreen)"
            >
              <span class="about-armory-screen-bar">
                <span><i class="fa-solid fa-shield-halved"></i> Blizzard Armory dossier</span>
                <span>Myzouht · Burning Legion EU</span>
                <i class="fa-solid fa-up-right-and-down-left-from-center"></i>
              </span>
              <Transition :name="`about-armory-swap-${armorySlideDirection}`">
                <img
                  :key="activeArmoryScreen.title"
                  :src="activeArmoryScreen.image"
                  :alt="activeArmoryScreen.alt"
                  loading="lazy"
                >
              </Transition>
            </button>

            <aside class="about-armory-detail">
              <span class="about-armory-detail-icon"><i :class="activeArmoryScreen.icon"></i></span>
              <p class="about-section-kicker">{{ activeArmoryScreen.kicker }}</p>
              <h3>{{ activeArmoryScreen.heading }}</h3>
              <p>{{ activeArmoryScreen.description }}</p>
              <ul>
                <li v-for="signal in activeArmoryScreen.signals" :key="signal">
                  <i class="fa-solid fa-check"></i>
                  {{ signal }}
                </li>
              </ul>
              <div class="about-armory-source">
                <img :src="wowServiceIcon" alt="">
                <span><strong>Blizzard profile data</strong><small>Source stays one click away</small></span>
              </div>
              <RouterLink class="about-text-link" to="/favorites">
                Open favorite characters
                <i class="fa-solid fa-arrow-right"></i>
              </RouterLink>
            </aside>
          </div>
        </div>
      </section>

      <section class="about-capability-section">
        <header class="about-section-heading">
          <p class="about-section-kicker">More than progression</p>
          <h2>A workspace that stays useful between runs.</h2>
          <p>Research the meta, follow the game, personalize the experience and keep every important profile close.</p>
        </header>

        <div class="about-capability-grid">
          <article
            v-for="capability in capabilities"
            :key="capability.title"
            class="about-capability-card"
            :class="capability.className"
          >
            <button type="button" @click="openScreenshot(capability)">
              <img :src="capability.image" :alt="capability.alt" loading="lazy">
              <span class="about-image-open"><i class="fa-solid fa-expand"></i></span>
            </button>
            <div>
              <span class="about-capability-icon"><i :class="capability.icon"></i></span>
              <p class="about-section-kicker">{{ capability.kicker }}</p>
              <h3>{{ capability.title }}</h3>
              <p>{{ capability.description }}</p>
              <RouterLink :to="capability.to" aria-label="Open feature">
                <i class="fa-solid fa-arrow-right"></i>
              </RouterLink>
            </div>
          </article>
        </div>
      </section>

      <section id="sources" class="about-source-section">
        <div class="about-source-copy">
          <p class="about-section-kicker">Connected where it counts</p>
          <h2>Trusted sources.<br>One clear interface.</h2>
          <p>
            Raider.io and WoW Armory supply the competitive and character data. Wowhead and
            WoWHub add the news, guides and community context around every decision.
          </p>
          <div class="about-source-promise">
            <i class="fa-solid fa-database"></i>
            <span><strong>Local-first lists</strong><small>Favorites and comparisons stay on your device.</small></span>
          </div>
        </div>

        <div class="about-source-grid">
          <a
            v-for="(source, index) in dataSources"
            :key="source.name"
            class="about-source-card"
            :href="source.href"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="`Open ${source.name} in a new tab`"
          >
            <span class="about-source-logo">
              <img v-if="source.image" :src="source.image" :alt="source.name" :class="source.logoClass">
              <i v-else :class="source.icon"></i>
            </span>
            <div><strong>{{ source.name }}</strong><small>{{ source.description }}</small></div>
            <span class="about-source-link-meta" aria-hidden="true">
              <b class="about-source-number">0{{ index + 1 }}</b>
              <i class="fa-solid fa-arrow-up-right-from-square"></i>
            </span>
          </a>
        </div>
      </section>

      <section class="about-final-cta">
        <div class="about-final-glow" aria-hidden="true"></div>
        <img :src="appLogo" alt="" aria-hidden="true">
        <p class="about-section-kicker">Ready when you are</p>
        <h2>Less tab hunting.<br>More time in Azeroth.</h2>
        <p>Start with the live Mythic+ dashboard or add the characters you care about.</p>
        <div class="about-hero-actions">
          <RouterLink class="btn about-primary-cta" to="/mythic-plus">
            Open Mythic+
            <i class="fa-solid fa-dungeon"></i>
          </RouterLink>
          <RouterLink class="btn-outline" to="/favorites">
            Build a watchlist
            <i class="fa-solid fa-star"></i>
          </RouterLink>
        </div>
      </section>
    </main>

    <footer class="about-landing-footer">
      <RouterLink class="about-landing-brand" to="/about">
        <span class="about-landing-logo"><img :src="appLogo" alt=""><small>2.0</small></span>
        <span><small>World of Warcraft</small><strong>Raider.io Dashboard</strong></span>
      </RouterLink>
      <p>This project uses the developer API from <a href="https://raider.io/" target="_blank" rel="noreferrer">Raider.io</a>.</p>
      <p>Designed and developed by <a href="https://andrii-ilutsa.me/" target="_blank" rel="noreferrer">Andrii Ilutsa</a>.</p>
    </footer>

    <Teleport to="body">
      <Transition name="about-lightbox">
        <div v-if="activeScreenshot" class="about-lightbox" role="dialog" aria-modal="true" @click.self="closeScreenshot">
          <div class="about-lightbox-dialog">
            <header>
              <div>
                <p class="about-section-kicker">Product screenshot</p>
                <h2>{{ activeScreenshot.title }}</h2>
              </div>
              <button type="button" aria-label="Close screenshot" @click="closeScreenshot">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </header>
            <div class="about-lightbox-scroll">
              <img :src="activeScreenshot.image" :alt="activeScreenshot.alt">
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script>
import appLogo from '../assets/images/logo.png'
import rioServiceIcon from '../assets/images/services/rio-small-logo.svg'
import wowServiceIcon from '../assets/images/services/wow-small-logo.svg'
import wowheadServiceIcon from '../assets/images/services/wowhead-logo.png'
import wowhubServiceIcon from '../assets/images/services/wowhub-logo.png'
import buildInsightsImage from '../assets/images/landing/build-insights.png'
import compareImage from '../assets/images/landing/compare.png'
import favoritesGridImage from '../assets/images/landing/favorites-grid.png'
import favoritesListImage from '../assets/images/landing/favorites-list.png'
import mythicPlusImage from '../assets/images/landing/mythic-plus.png'
import mythicPlusAltImage from '../assets/images/landing/mythic-plus-alt.png'
import newsImage from '../assets/images/landing/news.png'
import raidingImage from '../assets/images/landing/raiding.png'
import settingsImage from '../assets/images/landing/settings.png'
import armoryOverviewImage from '../assets/images/landing/armory/2.png'
import armoryGearImage from '../assets/images/landing/armory/3.png'
import armoryTalentsImage from '../assets/images/landing/armory/4.png'
import armoryRunsImage from '../assets/images/landing/armory/5.png'
import armoryReputationImage from '../assets/images/landing/armory/6.png'

export default {
  name: 'AboutView',
  data() {
    return {
      appLogo,
      wowServiceIcon,
      activeScreenshot: null,
      activeArmoryIndex: 0,
      armorySlideDirection: 'next',
      isNavCompact: false,
      heroScreen: {
        title: 'Mythic Plus progression',
        image: mythicPlusImage,
        alt: 'Mythic Plus dashboard with season cutoffs, timed key statistics and class distribution',
      },
      proofPoints: [
        { value: '6', label: 'focused workflows', icon: 'fa-solid fa-layer-group' },
        { value: 'Live', label: 'season data', icon: 'fa-solid fa-bolt' },
        { value: 'Local', label: 'saved profiles', icon: 'fa-solid fa-shield-halved' },
        { value: 'PWA', label: 'desktop & mobile', icon: 'fa-solid fa-mobile-screen-button' },
      ],
      contextQuestions: [
        { label: 'Readiness', text: 'Who is ready for the next key?', icon: 'fa-solid fa-dungeon' },
        { label: 'Progress', text: 'Where is the raid race moving?', icon: 'fa-solid fa-flag-checkered' },
        { label: 'Meta', text: 'What are top players building?', icon: 'fa-solid fa-wand-magic-sparkles' },
      ],
      productStories: [
        {
          kicker: 'Mythic+ intelligence',
          title: 'See the shape of the season, not just a score.',
          description: 'Cutoffs, timed keys, dungeon distribution and class representation come together in one statistics-first view.',
          points: ['Track score thresholds at a glance', 'Compare dungeon and class distribution', 'Move from overview to live Raider.io context'],
          cta: 'Explore Mythic+',
          to: '/mythic-plus',
          image: mythicPlusImage,
          position: 'top center',
          alt: 'Mythic Plus dashboard with score cutoffs, dungeon statistics and class distribution',
          activeVariant: 0,
          slideDirection: 'next',
          variants: [
            {
              label: 'Statistics', icon: 'fa-solid fa-chart-simple', title: 'Mythic Plus statistics',
              image: mythicPlusImage, position: 'top center',
              alt: 'Mythic Plus statistics with score cutoffs, dungeon distribution and class representation',
            },
            {
              label: 'Ladder', icon: 'fa-solid fa-table-list', title: 'Mythic Plus ladder',
              image: mythicPlusAltImage, position: 'top center',
              alt: 'Mythic Plus ladder with timed runs, party roles, affixes and scores',
            },
          ],
        },
        {
          kicker: 'Raid progression',
          title: 'Follow the race from guild ranking to boss pull.',
          description: 'Read the current raid at two levels at once: who is leading overall, and how the field is progressing through every boss.',
          points: ['Filter by expansion, raid and difficulty', 'Review guild standings and boss completion', 'Open videos and source pages from the same view'],
          cta: 'Open raid progression',
          to: '/raiding',
          image: raidingImage,
          position: 'top center',
          alt: 'Raiding dashboard with guild rankings and boss progression cards',
        },
        {
          kicker: 'Roster vault',
          title: 'Turn interesting characters into a working shortlist.',
          description: 'Save a Raider.io profile once, then use the same local roster for quick checks, refreshes and side-by-side decisions.',
          points: ['Switch between visual cards and compact lists', 'Keep score, item level and raid progress visible', 'Compare gear and rankings column by column'],
          cta: 'Create your watchlist',
          to: '/favorites',
          image: favoritesGridImage,
          position: 'top center',
          alt: 'Favorite character cards with score, item level, rankings and gear links',
          companion: {
            title: 'Character compare',
            image: favoritesListImage,
            alt: 'Side-by-side comparison of character stats and equipped gear',
          },
        },
        {
          kicker: 'Build insights',
          title: 'Build from what top players actually equip.',
          description: 'Aggregated leaderboard samples reveal the gear, talents and stat signals that define a specialization right now.',
          points: ['Filter class and specialization quickly', 'Inspect best-in-slot adoption and coverage', 'Move between equipment, talents, stats and players'],
          cta: 'Research a build',
          to: '/gear-insights',
          image: buildInsightsImage,
          position: 'top center',
          alt: 'Build Insights dashboard showing classes, specializations and recommended equipment',
        },
      ],
      armoryScreens: [
        {
          title: 'Overview', label: 'Stats at a glance', kicker: 'Character overview',
          heading: 'Read the whole stat line in seconds.',
          description: 'Primary resources, secondary ratings and detailed attributes are organized into a compact readiness check.',
          signals: ['Primary and secondary stats', 'Detailed combat attributes', 'Visible data freshness'],
          image: armoryOverviewImage, icon: 'fa-solid fa-chart-simple',
          alt: 'Blizzard Armory character overview with primary, secondary and detailed stats',
        },
        {
          title: 'Gear', label: 'Equipped loadout', kicker: 'Character gear',
          heading: 'Inspect every equipped choice around the character.',
          description: 'The full loadout pairs item names and levels with a character render, making missing or unusual slots immediately visible.',
          signals: ['Complete equipped loadout', 'Per-slot item levels', 'Character model in context'],
          image: armoryGearImage, icon: 'fa-solid fa-shirt',
          alt: 'Blizzard Armory gear view with character model and equipped items',
        },
        {
          title: 'Talents', label: 'Active build', kicker: 'Talent loadout',
          heading: 'See the build, then take it to the calculator.',
          description: 'Active class, hero and specialization trees stay readable as one build, with direct handoff to deeper Wowhead tools.',
          signals: ['All talent trees together', 'Active nodes highlighted', 'Direct Wowhead handoff'],
          image: armoryTalentsImage, icon: 'fa-solid fa-code-branch',
          alt: 'Blizzard Armory talent view with active Death Knight talent trees',
        },
        {
          title: 'M+ Runs', label: 'Dungeon history', kicker: 'Mythic Plus history',
          heading: 'Turn a score into the runs that earned it.',
          description: 'Best and recent dungeon results expose key level, score, clear time and date instead of leaving performance as a single number.',
          signals: ['Best and recent runs', 'Key level and score', 'Clear time and completion date'],
          image: armoryRunsImage, icon: 'fa-solid fa-dungeon',
          alt: 'Blizzard Armory Mythic Plus view with best dungeon runs and scores',
        },
        {
          title: 'Reputation', label: 'Faction progress', kicker: 'World progress',
          heading: 'Keep the long game visible too.',
          description: 'Expansion groups and faction progress reveal the character beyond combat performance and current-season rankings.',
          signals: ['Expansion-based grouping', 'Faction progress bars', 'Exact standing values'],
          image: armoryReputationImage, icon: 'fa-solid fa-handshake',
          alt: 'Blizzard Armory reputation view with faction progress grouped by expansion',
        },
      ],
      capabilities: [
        {
          kicker: 'Warcraft feed', title: 'News without the noise',
          description: 'Wowhead stories, blue posts, categories and translated summaries in a dense, readable feed.',
          image: newsImage, alt: 'News dashboard with a grid of World of Warcraft articles',
          icon: 'fa-solid fa-newspaper', to: '/news', className: 'is-wide',
        },
        {
          kicker: 'Character lab', title: 'Compare every meaningful detail',
          description: 'Score, ranks, raid progress and eighteen gear slots line up in a decision-ready table.',
          image: compareImage, alt: 'Character comparison dashboard with metrics and gear rows',
          icon: 'fa-solid fa-scale-balanced', to: '/compare', className: '',
        },
        {
          kicker: 'Control room', title: 'Make the dashboard yours',
          description: 'Theme, install, update and backdrop controls keep the app feeling native on every device.',
          image: settingsImage, alt: 'Application settings with appearance and background controls',
          icon: 'fa-solid fa-sliders', to: '/settings', className: '',
        },
      ],
      dataSources: [
        {
          name: 'Raider.io',
          description: 'Profiles, rankings, runs and progression',
          image: rioServiceIcon,
          href: 'https://raider.io/',
        },
        {
          name: 'WoW Armory',
          description: 'Stats, equipment, talents and character progress',
          image: wowServiceIcon,
          href: 'https://worldofwarcraft.blizzard.com/en-gb/search/character',
        },
        {
          name: 'Wowhead',
          description: 'News, guides, items and talent tools',
          image: wowheadServiceIcon,
          logoClass: 'is-wowhead',
          href: 'https://www.wowhead.com/',
        },
        {
          name: 'WoWHub',
          description: 'Ukrainian news, guides and community tools',
          image: wowhubServiceIcon,
          href: 'https://wowhub.com.ua/',
        },
      ],
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
    window.addEventListener('scroll', this.onScroll, { passive: true })
    this.onScroll()
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('scroll', this.onScroll)
    document.body.classList.remove('about-lightbox-open')
  },
  computed: {
    activeArmoryScreen() {
      return this.armoryScreens[this.activeArmoryIndex]
    },
  },
  methods: {
    getStoryScreen(story) {
      if (!story.variants) return story
      return story.variants[story.activeVariant] || story.variants[0]
    },
    setStoryVariant(story, index) {
      if (story.activeVariant === index) return
      story.slideDirection = index > story.activeVariant ? 'next' : 'previous'
      story.activeVariant = index
    },
    setArmoryScreen(index) {
      if (this.activeArmoryIndex === index) return
      this.armorySlideDirection = index > this.activeArmoryIndex ? 'next' : 'previous'
      this.activeArmoryIndex = index
    },
    scrollToSection(id) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    openScreenshot(screen) {
      this.activeScreenshot = screen
      document.body.classList.add('about-lightbox-open')
    },
    closeScreenshot() {
      this.activeScreenshot = null
      document.body.classList.remove('about-lightbox-open')
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.activeScreenshot) this.closeScreenshot()
    },
    onScroll() {
      const compactThreshold = this.isNavCompact ? 24 : 80
      const shouldCompact = window.scrollY > compactThreshold
      if (shouldCompact !== this.isNavCompact) this.isNavCompact = shouldCompact
    },
  },
}
</script>
